import {
  Component,
  inject,
  input,
  OnDestroy,
  OnInit,
  QueryList,
  signal,
  ViewChild,
  ViewChildren,
  WritableSignal,
} from '@angular/core';
import { Observable, of, Subscription } from 'rxjs';

import { TranslateService } from '@ngx-translate/core';
import { cloneDeep, intersectionWith, isEqual, sortBy, union } from 'lodash';
import { ZiroIcons } from '../../../../../shared/models/ziro-icons.enum';
import { RangeService } from '../../../../../shared/services/range.service';
import {
  ExtensionRangesDisplayFormComponent,
  RangeDisplayFormModel,
} from '../../../../../forms/fields/extension-ranges/extension-ranges-display-form/extension-ranges-display-form.component';
import {
  requiredHiddenFieldValidator,
  ZiroCustomInputTextConfig,
} from '../../../../../shared/custom-configs/custom-input-text/ziro-custom-input-text.component';
import { VariableEditorType } from '../../../../../modals/edit-custom-input-text-modal/variable-editor.service';
import {
  ZiroCustomSelectComponent,
  ZiroCustomSelectConfig,
} from '../../../../../shared/custom-configs/custom-select/ziro-custom-select.component';
import {
  Checkbox,
  CiscoFrontEndField,
  CucmMetadata,
  CustomCheckbox,
  CustomInputText,
  CustomMultiSelect,
  CustomSelect,
  DialPlanManagementGroup,
  DnDidRange,
  SiteResult,
  SiteSummary,
} from '../../../../../shared/models/generated/ziroModels';
import { ZiroFormAbstractDirective } from '../../../../../forms/ziro-form-abstract.directive';
import {
  ZiroFormConfig,
  ZiroFormsAutogenerationConfig,
  ZiroFormsUpdate,
  ZiroFormsValidationConfig,
  ZiroFormsValidationState,
} from '../../../../../forms/ziro-forms-models';
import { ExtensionRangesOptionalValidators } from '../../../../../forms/fields/extension-ranges/ziro-extension-range-models';
import { ZiroTextareaConfig } from '../../../../../forms/fields/textarea/ziro-textarea.component';
import { HtmlCheckboxType, ZiroCheckboxConfig } from '../../../../../forms/fields/checkbox/ziro-checkbox.component';
import { ZiroMultiCheckboxConfig } from '../../../../../forms/fields/multi-checkbox/ziro-multi-checkbox.component';
import { ButtonStyles } from '../../../../../button/button.component';

export type ValidationTarget = 'VALID' | 'INVALID';

interface CustomCheckboxUi {
  enabled: boolean;
  show: boolean;
}

export interface CiscoDnRangesFormEntity {
  // DN Ranges
  ranges: DnDidRange[];
  // Line Appearance Settings
  advertise_distinct_mask_for_public_lines: Checkbox;
  external_phone_number_mask: CustomInputText;
  public_external_phone_number_mask: CustomInputText;
  line_text_label: CustomInputText;
  // Directory Number Settings
  route_partition: CustomSelect;
  urgent_priority: CustomCheckbox;
  optional_line_group_membership: CustomMultiSelect;
  auto_answer: CustomSelect;
  monitoring_calling_search_space: CustomSelect;
  // Enterprise Alternate Number
  ent_alt_num_number_mask: CustomInputText;
  ent_alt_num_advertise_globally_via_ils: CustomCheckbox;
  ent_alt_num_is_urgent: CustomCheckbox;
  ent_alt_num_add_to_local_route_partition: CustomCheckbox;
  ent_alt_num_route_partition: CustomSelect;
  // E.164 Alternate Number
  e164_alt_num_number_mask: CustomInputText;
  e164_alt_num_advertise_globally_via_ils: CustomCheckbox;
  e164_alt_num_is_urgent: CustomCheckbox;
  e164_alt_num_add_to_local_route_partition: CustomCheckbox;
  e164_alt_num_route_partition: CustomSelect;
  // PSTN Failover
  pstn_failover: CustomSelect;
  // Voicemail Alternate Extensions
  enableAlternateExtension: boolean;
  voicemailAlternateExtension: CustomInputText;
}

export interface CiscoDnRangesFormData {
  // DN Ranges
  ranges: DnDidRange[];
  bulkRanges: '';
  // Line Appearance Settings
  advertise_distinct_mask_for_public_lines: boolean;
  external_phone_number_mask: CustomInputText;
  public_external_phone_number_mask: CustomInputText;
  line_text_label: CustomInputText;
  // Other Directory Number Settings
  route_partition: CustomSelect;
  urgent_priority: CustomCheckboxUi;
  optional_line_group_membership?: CustomMultiSelect;
  auto_answer: CustomSelect;
  monitoring_calling_search_space: CustomSelect;
  // Enterprise Alternate Number
  ent_alt_num_number_mask: CustomInputText;
  ent_alt_num_advertise_globally_via_ils: CustomCheckboxUi;
  ent_alt_num_is_urgent: CustomCheckboxUi;
  ent_alt_num_add_to_local_route_partition: CustomCheckboxUi;
  ent_alt_num_route_partition: CustomSelect;
  // E.164 Alternate Number
  e164_alt_num_number_mask: CustomInputText;
  e164_alt_num_advertise_globally_via_ils: CustomCheckboxUi;
  e164_alt_num_is_urgent: CustomCheckboxUi;
  e164_alt_num_add_to_local_route_partition: CustomCheckboxUi;
  e164_alt_num_route_partition: CustomSelect;
  // PSTN Failover
  pstn_failover: CustomSelect;
  // Voicemail Alternate Extensions
  enableAlternateExtension: boolean;
  voicemailAlternateExtension: CustomInputText;
}

@Component({
  selector: 'ziro-cisco-dial-plan-management-dn-ranges-form',
  templateUrl: './dial-plan-management-dn-ranges-form.component.html',
  styleUrls: ['./dial-plan-management-dn-ranges-form.component.scss'],
  standalone: false,
})
export class CiscoDialPlanManagementDnRangesFormComponent
  extends ZiroFormAbstractDirective<CiscoDnRangesFormEntity, CiscoDnRangesFormData>
  implements OnInit, OnDestroy
{
  private _translateService = inject(TranslateService);

  readonly groupId = input<number>();
  readonly clusterId = input<number>();
  readonly dialPlanGroups = input<DialPlanManagementGroup[]>([]);
  readonly cucmMetadata = input<CucmMetadata>();
  readonly siteSummary = input<SiteSummary>();
  readonly selectedSites = input<SiteResult[]>();

  @ViewChildren(ZiroCustomSelectComponent) childSelects: QueryList<ZiroCustomSelectComponent>;
  @ViewChild(ExtensionRangesDisplayFormComponent) extensionRangesDisplayForm: ExtensionRangesDisplayFormComponent;

  ziroIcons = ZiroIcons;
  validationStates = ZiroFormsValidationState;
  formConfig: ZiroFormConfig;
  isEntAltSectionVisible = false;
  isE164AltSectionVisible = false;
  isPstnFailoverSectionVisible = false;
  isVoicemailAlternateExtensionSectionVisible = false;
  displayRangeFormEntity: RangeDisplayFormModel;
  displayRangeForm = false;
  extensionRangesDisplayFormValidationState: ZiroFormsValidationState;
  displayRangeFormOptionalValidators: ExtensionRangesOptionalValidators;
  fieldIdSignalOptions: Partial<{ [K in keyof CiscoDnRangesFormData]: WritableSignal<any> }> = {
    pstn_failover: signal<string[]>(null),
  };

  private _subscriptions = new Subscription();

  private static _isTextConfigPopulated(config: CustomInputText): boolean {
    return !!(config.required || config.show || config.value);
  }

  private static _isSelectConfigPopulated(config: CustomSelect): boolean {
    return !!(config.possibleOptions.length || config.defaultOption || config.required || config.show);
  }

  private static _isCheckboxConfigPopulated(config: CustomCheckbox): boolean {
    return !!(config.value || config.show);
  }

  private static _getInitialPstnOptions(hasEnterprise: boolean, hasE164: boolean): string[] {
    const options = [];
    if (hasEnterprise) {
      options.push('Enterprise Number');
    }

    if (hasE164) {
      options.push('+E.164 Number');
    }

    return options;
  }

  private static _getEntAltSectionPopulated(entity: CiscoDnRangesFormEntity): boolean {
    return (
      CiscoDialPlanManagementDnRangesFormComponent._isTextConfigPopulated(
        entity.ent_alt_num_number_mask as CustomInputText,
      ) ||
      CiscoDialPlanManagementDnRangesFormComponent._isCheckboxConfigPopulated(
        entity.ent_alt_num_advertise_globally_via_ils as CustomCheckbox,
      ) ||
      CiscoDialPlanManagementDnRangesFormComponent._isCheckboxConfigPopulated(
        entity.ent_alt_num_is_urgent as CustomCheckbox,
      ) ||
      CiscoDialPlanManagementDnRangesFormComponent._isCheckboxConfigPopulated(
        entity.ent_alt_num_add_to_local_route_partition as CustomCheckbox,
      ) ||
      CiscoDialPlanManagementDnRangesFormComponent._isSelectConfigPopulated(
        entity.ent_alt_num_route_partition as CustomSelect,
      )
    );
  }

  private static _getE164AltSectionPopulated(entity: CiscoDnRangesFormEntity): boolean {
    return (
      CiscoDialPlanManagementDnRangesFormComponent._isTextConfigPopulated(
        entity.e164_alt_num_number_mask as CustomInputText,
      ) ||
      CiscoDialPlanManagementDnRangesFormComponent._isCheckboxConfigPopulated(
        entity.e164_alt_num_advertise_globally_via_ils as CustomCheckbox,
      ) ||
      CiscoDialPlanManagementDnRangesFormComponent._isCheckboxConfigPopulated(
        entity.e164_alt_num_is_urgent as CustomCheckbox,
      ) ||
      CiscoDialPlanManagementDnRangesFormComponent._isSelectConfigPopulated(
        entity.e164_alt_num_route_partition as CustomSelect,
      ) ||
      CiscoDialPlanManagementDnRangesFormComponent._isCheckboxConfigPopulated(
        entity.e164_alt_num_add_to_local_route_partition as CustomCheckbox,
      )
    );
  }

  private static _getPstnFailoverSectionPopulated(entity: CiscoDnRangesFormEntity): boolean {
    return CiscoDialPlanManagementDnRangesFormComponent._isSelectConfigPopulated(entity.pstn_failover as CustomSelect);
  }

  private static _isLocalRoutePartitionsEnabled(formData: CiscoDnRangesFormData): boolean {
    const entNumberMask = formData.ent_alt_num_number_mask ? formData.ent_alt_num_number_mask : null;
    const entAdvertiseGlobally = formData.ent_alt_num_advertise_globally_via_ils
      ? (formData.ent_alt_num_advertise_globally_via_ils as CustomCheckboxUi)
      : null;
    const entIsUrgent = formData.ent_alt_num_is_urgent ? (formData.ent_alt_num_is_urgent as CustomCheckboxUi) : null;
    const entAddToLocalRoutePartition = formData.ent_alt_num_add_to_local_route_partition
      ? (formData.ent_alt_num_add_to_local_route_partition as CustomCheckboxUi)
      : null;

    const e164NumberMask = formData.e164_alt_num_number_mask ? formData.e164_alt_num_number_mask : null;
    const e164AdvertiseGlobally = formData.e164_alt_num_advertise_globally_via_ils
      ? (formData.e164_alt_num_advertise_globally_via_ils as CustomCheckboxUi)
      : null;
    const e164IsUrgent = formData.e164_alt_num_is_urgent ? (formData.e164_alt_num_is_urgent as CustomCheckboxUi) : null;
    const e164AddToLocalRoutePartition = formData.e164_alt_num_add_to_local_route_partition
      ? (formData.e164_alt_num_add_to_local_route_partition as CustomCheckboxUi)
      : null;

    return (
      // Enterprise
      (entNumberMask && (!!entNumberMask.value || entNumberMask.required || entNumberMask.show)) ||
      (entAdvertiseGlobally && (entAdvertiseGlobally.show || entAdvertiseGlobally.enabled)) ||
      (entIsUrgent && (entIsUrgent.show || entIsUrgent.enabled)) ||
      (entAddToLocalRoutePartition && (entAddToLocalRoutePartition.show || entAddToLocalRoutePartition.enabled)) ||
      // E.164
      (e164NumberMask && (!!e164NumberMask.value || e164NumberMask.required || e164NumberMask.show)) ||
      (e164AdvertiseGlobally && (e164AdvertiseGlobally.show || e164AdvertiseGlobally.enabled)) ||
      (e164IsUrgent && (e164IsUrgent.show || e164IsUrgent.enabled)) ||
      (e164AddToLocalRoutePartition && (e164AddToLocalRoutePartition.show || e164AddToLocalRoutePartition.enabled))
    );
  }

  private static _isE164LocalRoutePartitionEnabledOrShowing(entity: CiscoDnRangesFormEntity): boolean {
    return (
      entity.e164_alt_num_add_to_local_route_partition.value || entity.e164_alt_num_add_to_local_route_partition.show
    );
  }

  private static _isEANLocalRoutePartitionEnabledOrShowing(entity: CiscoDnRangesFormEntity): boolean {
    return (
      entity.ent_alt_num_add_to_local_route_partition.value || entity.ent_alt_num_add_to_local_route_partition.show
    );
  }

  ngOnInit(): void {
    const entAltSectionPopulated = CiscoDialPlanManagementDnRangesFormComponent._getEntAltSectionPopulated(
      this.entity as CiscoDnRangesFormEntity,
    );
    const e164AltSectionPopulated = CiscoDialPlanManagementDnRangesFormComponent._getE164AltSectionPopulated(
      this.entity as CiscoDnRangesFormEntity,
    );

    this.fieldIdSignalOptions.pstn_failover.set(
      CiscoDialPlanManagementDnRangesFormComponent._getInitialPstnOptions(
        entAltSectionPopulated,
        e164AltSectionPopulated,
      ),
    );

    this.isEntAltSectionVisible = entAltSectionPopulated;
    this.isE164AltSectionVisible = e164AltSectionPopulated;
    this.isPstnFailoverSectionVisible = CiscoDialPlanManagementDnRangesFormComponent._getPstnFailoverSectionPopulated(
      this.entity as CiscoDnRangesFormEntity,
    );
    this.isVoicemailAlternateExtensionSectionVisible = this.entity.enableAlternateExtension;

    this.formConfig = {
      fields: {
        bulkRanges: {
          label: 'tkey;dialplanmanagement.admin.group.generate.dnranges',
          helpText: () => 'tkey;dialplanmanagement.admin.group.generate.dnranges.helpblock',
          dataAutomation: 'dn-ranges-bulk-ranges',
          componentConfig: new ZiroTextareaConfig({
            placeholder: this._translateService.instant('tkey;admin.msdialplan.management.bulk_generate.placeholder'),
          }),
          validation: [
            {
              validator: (val: string) => {
                if (!val) {
                  return ZiroFormsValidationState.VALID;
                }

                const split = val.split(',');
                const hasInvalid = split.some((v: string) => {
                  return !/^\+?[0-9]+(?:-\+?[0-9]+)*$/.test(v.trim());
                });

                return hasInvalid ? ZiroFormsValidationState.INVALID : ZiroFormsValidationState.VALID;
              },
              message: 'tkey;rangeDirective.range.unsupportedpattern',
            },
          ],
        },
        // Line Appearance Settings
        advertise_distinct_mask_for_public_lines: {
          label: 'tkey;dialplanmanagement.admin.group.dnranges.external_phone_mask.advertise_public_line_mask.label',
          dataAutomation: 'dn-ranges-advertise-distinct-mask-for-public-lines',
          componentConfig: new ZiroCheckboxConfig({ checkboxType: HtmlCheckboxType.SWITCH }),
          helpText:
            'tkey;dialplanmanagement.admin.group.dnranges.external_phone_mask.advertise_public_line_mask.help_text',
          labelToolTipText:
            'tkey;dialplanmanagement.admin.group.dnranges.external_phone_mask.advertise_public_line_mask.tooltip',
          labelToolTipIconClass: this.ziroIcons.TOOLTIP,
        },
        external_phone_number_mask: {
          dataAutomation: 'dn-ranges-external-phone-number-mask',
          componentConfig: new ZiroCustomInputTextConfig({
            editorType: VariableEditorType.CISCO,
            isAlwaysRequired: false,
            hideCheckboxes: false,
            hideEditorButton: true,
          }),
          label: () => {
            return this.formData.advertise_distinct_mask_for_public_lines
              ? 'tkey;dialplanmanagement.admin.group.dnranges.external_phone_mask.user_lines.label'
              : 'tkey;site_management.site.section.field.external_phone_number_mask.text';
          },
          helpText: () =>
            this.formData.advertise_distinct_mask_for_public_lines
              ? 'tkey;dialplanmanagement.admin.group.dnranges.external_phone_mask.user_lines.help_text'
              : '',
          validation: [requiredHiddenFieldValidator],
        },
        public_external_phone_number_mask: {
          label: 'tkey;dialplanmanagement.admin.group.dnranges.external_phone_mask.public_lines.label',
          dataAutomation: 'dn-ranges-public-external-phone-number-mask',
          componentConfig: new ZiroCustomInputTextConfig({
            editorType: VariableEditorType.CISCO,
            isAlwaysRequired: false,
            hideCheckboxes: false,
            hideEditorButton: true,
          }),
          helpText: () => 'tkey;dialplanmanagement.admin.group.dnranges.external_phone_mask.public_lines.help_text',
          hidden: () => !this.formData.advertise_distinct_mask_for_public_lines,
          validation: [requiredHiddenFieldValidator],
        },
        line_text_label: {
          label: 'tkey;dialplanmanagement.admin.group.dnranges.line_text_label.label',
          dataAutomation: 'dn-ranges-line-text-label',
          componentConfig: new ZiroCustomInputTextConfig({
            editorType: VariableEditorType.CISCO,
            isAlwaysRequired: false,
            hideCheckboxes: false,
            hideEditorButton: false,
          }),
          validation: [requiredHiddenFieldValidator],
        },
        // Directory Number Settings
        route_partition: {
          label: 'tkey;dialplanmanagement.admin.group.dnranges.e164_alternate_number.route_partition.label',
          dataAutomation: 'dn-ranges-route-partition',
          componentConfig: new ZiroCustomSelectConfig({
            modalHeader: 'tkey;dialplanmanagement.admin.group.dnranges.e164_alternate_number.route_partition.label',
            displayValues: sortBy(this.cucmMetadata().routePartitions),
            availableOptions: sortBy(this.cucmMetadata().routePartitions),
            isAlwaysRequired: false,
          }),
          validation: this._getCustomSelectValidation('route_partition', this.cucmMetadata().routePartitions),
          autogeneration: this._getCustomSelectAutogeneration('route_partition', this.cucmMetadata().routePartitions),
        },
        urgent_priority: {
          label: 'tkey;dialplanmanagement.admin.group.dnranges.urgent_priority',
          dataAutomation: 'dn-ranges-urgent-priority',
          componentConfig: new ZiroMultiCheckboxConfig({
            multiCheckboxOptionConfig: [
              {
                label: 'tkey;site_management.site.section.checkbox.enable',
              },
              {
                label: 'tkey;site_management.site.section.show.label',
              },
            ],
          }),
        },
        optional_line_group_membership: {
          label: 'tkey;dialplanmanagement.admin.group.dnranges.line_groups.label',
          dataAutomation: 'dn-ranges-optional-line-group-membership',
          componentConfig: new ZiroCustomSelectConfig({
            modalHeader: 'tkey;dialplanmanagement.admin.group.dnranges.line_groups.label',
            displayValues: sortBy(this.cucmMetadata().lineGroups),
            availableOptions: sortBy(this.cucmMetadata().lineGroups),
            isAlwaysRequired: false,
            isMultiSelect: true,
            isRequiredVisible: false,
          }),
          validation: this._getCustomSelectValidation(
            'optional_line_group_membership',
            this.cucmMetadata().lineGroups,
            true,
          ),
          autogeneration: this._getCustomSelectAutogeneration(
            'optional_line_group_membership',
            this.cucmMetadata().lineGroups,
          ),
        },
        auto_answer: {
          label: 'tkey;dialplanmanagement.admin.group.dnranges.auto_answer.label',
          dataAutomation: 'dn-ranges-auto-answer',
          componentConfig: new ZiroCustomSelectConfig({
            modalHeader: 'tkey;dialplanmanagement.admin.group.dnranges.auto_answer.label',
            displayValues: sortBy(this.cucmMetadata().autoAnswers),
            availableOptions: sortBy(this.cucmMetadata().autoAnswers),
            isAlwaysRequired: true,
          }),
          validation: this._getCustomSelectValidation('auto_answer', this.cucmMetadata().autoAnswers),
          autogeneration: this._getCustomSelectAutogeneration('auto_answer', this.cucmMetadata().autoAnswers),
        },
        monitoring_calling_search_space: {
          label: 'tkey;dialplanmanagement.admin.group.dnranges.monitoring_css.label',
          dataAutomation: 'dn-ranges-monitoring-calling-search-space',
          componentConfig: new ZiroCustomSelectConfig({
            modalHeader: 'tkey;dialplanmanagement.admin.group.dnranges.monitoring_css.label',
            displayValues: sortBy(this.cucmMetadata().callingSearchSpaces),
            availableOptions: sortBy(this.cucmMetadata().callingSearchSpaces),
            isAlwaysRequired: false,
          }),
          validation: this._getCustomSelectValidation(
            'monitoring_calling_search_space',
            this.cucmMetadata().callingSearchSpaces,
          ),
          autogeneration: this._getCustomSelectAutogeneration(
            'monitoring_calling_search_space',
            this.cucmMetadata().callingSearchSpaces,
          ),
        },
        // Enterprise Alternate Number
        ent_alt_num_number_mask: {
          label: 'tkey;dialplanmanagement.admin.group.dnranges.e164_alternate_number.number_mask.label',
          dataAutomation: 'dn-ranges-ent-alt-num-external-phone-number-mask',
          componentConfig: new ZiroCustomInputTextConfig({
            editorType: VariableEditorType.CISCO,
            isAlwaysRequired: false,
            hideCheckboxes: false,
            hideEditorButton: false,
          }),
          validation: [requiredHiddenFieldValidator],
        },
        ent_alt_num_advertise_globally_via_ils: {
          label: 'tkey;dialplanmanagement.admin.group.dnranges.e164_alternate_number.advertise_globally.label',
          dataAutomation: 'dn-ranges-ent-alt-num-advertise-globally-via-ils',
          componentConfig: new ZiroMultiCheckboxConfig({
            multiCheckboxOptionConfig: [
              {
                label: 'tkey;site_management.site.section.checkbox.enable',
              },
              {
                label: 'tkey;site_management.site.section.show.label',
              },
            ],
          }),
        },
        ent_alt_num_is_urgent: {
          label: 'tkey;dialplanmanagement.admin.group.dnranges.e164_alternate_number.is_urgent.label',
          dataAutomation: 'dn-ranges-ent-alt-num-is-urgent',
          componentConfig: new ZiroMultiCheckboxConfig({
            multiCheckboxOptionConfig: [
              {
                label: 'tkey;site_management.site.section.checkbox.enable',
              },
              {
                label: 'tkey;site_management.site.section.show.label',
              },
            ],
          }),
        },
        ent_alt_num_add_to_local_route_partition: {
          label: 'tkey;dialplanmanagement.admin.group.dnranges.e164_alternate_number.add_route_partition.label.label',
          dataAutomation: 'dn-ranges-ent-alt-num-add-to-local-route-partition',
          componentConfig: new ZiroMultiCheckboxConfig({
            multiCheckboxOptionConfig: [
              {
                label: 'tkey;site_management.site.section.checkbox.enable',
              },
              {
                label: 'tkey;site_management.site.section.show.label',
              },
            ],
          }),
        },
        ent_alt_num_route_partition: {
          label: 'tkey;dialplanmanagement.admin.group.dnranges.e164_alternate_number.route_partition.label',
          dataAutomation: 'dn-ranges-ent-alt-num-route-partition',
          componentConfig: new ZiroCustomSelectConfig({
            modalHeader: 'tkey;dialplanmanagement.admin.group.dnranges.e164_alternate_number.route_partition.label',
            displayValues: sortBy(this.cucmMetadata().routePartitions),
            availableOptions: sortBy(this.cucmMetadata().routePartitions),
            isAlwaysRequired: false,
          }),
          validation: this._getCustomSelectValidation(
            'ent_alt_num_route_partition',
            this.cucmMetadata().routePartitions,
          ),
          autogeneration: this._getCustomSelectAutogeneration(
            'ent_alt_num_route_partition',
            this.cucmMetadata().routePartitions,
          ),
          disabled: () => {
            if (this.formData.ent_alt_num_add_to_local_route_partition) {
              const fd = this.formData.ent_alt_num_add_to_local_route_partition as CustomCheckboxUi;
              return !fd.enabled && !fd.show;
            }
            return false;
          },
          disabledTooltip: 'tkey;dialplanmanagement.admin.group.dnranges.route_partition.disabled.tooltip',
        },
        // E.164 Alternate Number
        e164_alt_num_number_mask: {
          label: 'tkey;dialplanmanagement.admin.group.dnranges.e164_alternate_number.number_mask.label',
          dataAutomation: 'dn-ranges-e164-alt-num-external-phone-number-mask',
          componentConfig: new ZiroCustomInputTextConfig({
            editorType: VariableEditorType.CISCO,
            isAlwaysRequired: false,
            hideCheckboxes: false,
            hideEditorButton: false,
          }),
          validation: [requiredHiddenFieldValidator],
        },
        e164_alt_num_advertise_globally_via_ils: {
          label: 'tkey;dialplanmanagement.admin.group.dnranges.e164_alternate_number.advertise_globally.label',
          dataAutomation: 'dn-ranges-e164-alt-num-advertise-globally-via-ils',
          componentConfig: new ZiroMultiCheckboxConfig({
            multiCheckboxOptionConfig: [
              {
                label: 'tkey;site_management.site.section.checkbox.enable',
              },
              {
                label: 'tkey;site_management.site.section.show.label',
              },
            ],
          }),
        },
        e164_alt_num_is_urgent: {
          label: 'tkey;dialplanmanagement.admin.group.dnranges.e164_alternate_number.is_urgent.label',
          dataAutomation: 'dn-ranges-e164-alt-num-is-urgent',
          componentConfig: new ZiroMultiCheckboxConfig({
            multiCheckboxOptionConfig: [
              {
                label: 'tkey;site_management.site.section.checkbox.enable',
              },
              {
                label: 'tkey;site_management.site.section.show.label',
              },
            ],
          }),
        },
        e164_alt_num_add_to_local_route_partition: {
          label: 'tkey;dialplanmanagement.admin.group.dnranges.e164_alternate_number.add_route_partition.label.label',
          dataAutomation: 'dn-ranges-e164-alt-num-add-to-local-route-partition',
          componentConfig: new ZiroMultiCheckboxConfig({
            multiCheckboxOptionConfig: [
              {
                label: 'tkey;site_management.site.section.checkbox.enable',
              },
              {
                label: 'tkey;site_management.site.section.show.label',
              },
            ],
          }),
        },
        e164_alt_num_route_partition: {
          label: 'tkey;dialplanmanagement.admin.group.dnranges.e164_alternate_number.route_partition.label',
          dataAutomation: 'dn-ranges-e164-alt-num-route-partition',
          componentConfig: new ZiroCustomSelectConfig({
            modalHeader: 'tkey;dialplanmanagement.admin.group.dnranges.e164_alternate_number.route_partition.label',
            displayValues: sortBy(this.cucmMetadata().routePartitions),
            availableOptions: sortBy(this.cucmMetadata().routePartitions),
            isAlwaysRequired: false,
          }),
          validation: this._getCustomSelectValidation(
            'e164_alt_num_route_partition',
            this.cucmMetadata().routePartitions,
          ),
          autogeneration: this._getCustomSelectAutogeneration(
            'e164_alt_num_route_partition',
            this.cucmMetadata().routePartitions,
          ),
          disabled: () => {
            if (this.formData.e164_alt_num_add_to_local_route_partition) {
              const fd = this.formData.e164_alt_num_add_to_local_route_partition as CustomCheckboxUi;
              return !fd.enabled && !fd.show;
            }

            return false;
          },
          disabledTooltip: 'tkey;dialplanmanagement.admin.group.dnranges.route_partition.disabled.tooltip',
        },
        // PSTN Failover
        pstn_failover: {
          label: 'tkey;dialplanmanagement.admin.group.dnranges.pstn_failover.advertised_number.label',
          dataAutomation: 'dn-ranges-pstn-failover',
          componentConfig: new ZiroCustomSelectConfig({
            modalHeader: 'tkey;dialplanmanagement.admin.group.dnranges.pstn_failover.advertised_number.label',
            displayValues: this.fieldIdSignalOptions.pstn_failover(),
            availableOptions: this.fieldIdSignalOptions.pstn_failover(),
            isAlwaysRequired: false,
          }),
          validation: this._getCustomSelectValidation(
            'pstn_failover',
            null,
            false,
            this.fieldIdSignalOptions.pstn_failover,
          ),
          autogeneration: this._getCustomSelectAutogeneration(
            'pstn_failover',
            null,
            false,
            this.fieldIdSignalOptions.pstn_failover,
          ),
          disabled: () =>
            !CiscoDialPlanManagementDnRangesFormComponent._isLocalRoutePartitionsEnabled(
              this.formData as CiscoDnRangesFormData,
            ),
          disabledTooltip:
            'tkey;dialplanmanagement.admin.group.dnranges.pstn_failover.advertised_number.disabled_tooltip',
        },
        // Voicemail Alternate Extensions
        enableAlternateExtension: {
          label:
            'tkey;dialplanmanagement.admin.group.dnranges.voicemail_alternate_extension.enable_alternate_extension.label',
          helpText:
            'tkey;dialplanmanagement.admin.group.dnranges.voicemail_alternate_extension.enable_alternate_extension.helptext',
          labelToolTipText:
            'tkey;dialplanmanagement.admin.group.dnranges.voicemail_alternate_extension.enable_alternate_extension.tooltip',
          labelToolTipIconClass: this.ziroIcons.TOOLTIP,
          dataAutomation: 'dn-ranges-enable-alternate-extension',
          componentConfig: new ZiroCheckboxConfig({ checkboxType: HtmlCheckboxType.SWITCH }),
          disabled: () => {
            return !this.selectedSites()?.length || this.selectedSites().some((site) => !site.unityServerId);
          },
          disabledTooltip:
            'tkey;dialplanmanagement.admin.group.dnranges.voicemail_alternate_extension.enable_alternate_extension.disabled.site_service_not_enabled.tooltip',
        },
        voicemailAlternateExtension: {
          label: 'tkey;dialplanmanagement.admin.group.dnranges.voicemail_alternate_extension.alternate_extension.label',
          dataAutomation: 'dn-ranges-voicemail-alternate-extension',
          componentConfig: new ZiroCustomInputTextConfig({
            editorType: VariableEditorType.CISCO_EXTERNAL_CONTACT,
            isAlwaysRequired: true,
            hideCheckboxes: true,
            hidePreview: false,
          }),
          hidden: () => {
            return !this.formData.enableAlternateExtension;
          },
          valExcluded: () => !this.formData.enableAlternateExtension,
          validation: [
            {
              validator: (val: CustomInputText) => {
                return val?.value ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID;
              },
              message: 'tkey;validators.global.required.error',
            },
            {
              validator: (val: CustomInputText) => {
                if (!val?.value) {
                  return ZiroFormsValidationState.VALID;
                }
                return /^\s*{{\s*(touppercase |tolowercase |namecase |ascii )?(primaryextension|mailid)\s*}}\s*$/i.test(
                  val.value,
                )
                  ? ZiroFormsValidationState.INVALID
                  : ZiroFormsValidationState.VALID;
              },
              message:
                'tkey;dialplanmanagement.admin.group.dnranges.voicemail_alternate_extension.alternate_extension.validation.primary_extension_conflict',
            },
          ],
        },
      },
    };

    this.displayRangeFormOptionalValidators = {
      overlappingRanges: true,
      strictE164Validation: false,
      hasSameLengthValidation: true,
      maxDiffValidation: 99999,
      minLengthValidation: 1,
      maxLengthValidation: 24,
      overlappingRangesInOtherCiscoDialPlanGroups: {
        clusterId: this.clusterId(),
        dialPlanGroups: !this.groupId()
          ? this.dialPlanGroups()
          : this.dialPlanGroups().filter((group: DialPlanManagementGroup) => Number(group.id) !== this.groupId()),
        range: 'DN',
      },
    };

    this.displayRangeFormEntity = { ranges: cloneDeep(this.entity.ranges) };
    this.displayRangeForm = true;
    this._watchForChanges();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._subscriptions.unsubscribe();
  }

  generateBulkRangesClicked(): void {
    if (!this.formData['bulkRanges'].trim()) {
      return;
    }

    this.displayRangeForm = false;

    // Remove new lines, spaces
    const generatedRanges = RangeService.generateRangesFromArray(
      this.formData['bulkRanges'].replace(/\n|\r/g, '').replace(/ /g, '').split(','),
    );
    const currentRanges = cloneDeep(this.entity.ranges).filter((r: DnDidRange) => r.start && r.end);
    const updatedRanges = [...currentRanges, ...generatedRanges];
    const newEntity = {
      ...this.entity,
      ranges: updatedRanges,
    };

    this.formData['bulkRanges'] = '';
    this.entitySource.next(cloneDeep(newEntity));
    this.displayRangeFormEntity = { ranges: newEntity.ranges };

    setTimeout(() => {
      this.displayRangeForm = true;
    });
  }

  submit(): Observable<null> {
    this.setDisplayFormValidationState();
    return of(null);
  }

  toFormData = (entity: CiscoDnRangesFormEntity): CiscoDnRangesFormData => {
    return {
      // DN Ranges
      ranges: entity.ranges,
      bulkRanges: this.formData['bulkRanges'] || '',
      // Line Appearance Settings
      advertise_distinct_mask_for_public_lines: entity.advertise_distinct_mask_for_public_lines?.value,
      external_phone_number_mask: {
        name: entity.external_phone_number_mask?.name,
        value: entity.external_phone_number_mask?.value,
        required: entity.external_phone_number_mask?.required,
        show: entity.external_phone_number_mask?.show,
      },
      public_external_phone_number_mask: {
        name: entity.public_external_phone_number_mask?.name,
        value: entity.public_external_phone_number_mask?.value,
        required: entity.public_external_phone_number_mask?.required,
        show: entity.public_external_phone_number_mask?.show,
      },
      line_text_label: {
        name: entity.line_text_label?.name,
        value: entity.line_text_label?.value,
        required: entity.line_text_label?.required,
        show: entity.line_text_label?.show,
      },
      // Directory Number Settings
      route_partition: {
        name: entity.route_partition?.name,
        defaultOption: entity.route_partition?.defaultOption,
        possibleOptions: entity.route_partition?.possibleOptions,
        required: entity.route_partition?.required,
        show: entity.route_partition?.show,
      },
      urgent_priority: {
        enabled: entity.urgent_priority.value,
        show: entity.urgent_priority.show,
      },
      optional_line_group_membership: {
        name: entity.optional_line_group_membership?.name,
        defaultOptions: entity.optional_line_group_membership?.defaultOptions
          ? entity.optional_line_group_membership.defaultOptions
          : [],
        possibleOptions: entity.optional_line_group_membership?.possibleOptions,
        show: entity.optional_line_group_membership?.show,
      },
      auto_answer: {
        name: entity.auto_answer?.name,
        defaultOption: entity.auto_answer?.defaultOption,
        possibleOptions: entity.auto_answer?.possibleOptions,
        required: true,
        show: entity.auto_answer?.show,
      },
      monitoring_calling_search_space: {
        name: entity.monitoring_calling_search_space?.name,
        defaultOption: entity.monitoring_calling_search_space?.defaultOption,
        possibleOptions: entity.monitoring_calling_search_space?.possibleOptions,
        required: entity.monitoring_calling_search_space?.required,
        show: entity.monitoring_calling_search_space?.show,
      },
      // Enterprise Alternate Number
      ent_alt_num_number_mask: {
        name: entity.ent_alt_num_number_mask?.name,
        value: entity.ent_alt_num_number_mask?.value,
        required: entity.ent_alt_num_number_mask?.required,
        show: entity.ent_alt_num_number_mask?.show,
      },
      ent_alt_num_advertise_globally_via_ils: {
        enabled: entity.ent_alt_num_advertise_globally_via_ils?.value,
        show: entity.ent_alt_num_advertise_globally_via_ils?.show,
      },
      ent_alt_num_is_urgent: {
        enabled: entity.ent_alt_num_is_urgent?.value,
        show: entity.ent_alt_num_is_urgent?.show,
      },
      ent_alt_num_add_to_local_route_partition: {
        enabled: entity.ent_alt_num_add_to_local_route_partition?.value,
        show: entity.ent_alt_num_add_to_local_route_partition?.show,
      },
      ent_alt_num_route_partition: {
        name: entity.ent_alt_num_route_partition?.name,
        defaultOption: CiscoDialPlanManagementDnRangesFormComponent._isEANLocalRoutePartitionEnabledOrShowing(entity)
          ? entity.ent_alt_num_route_partition?.defaultOption
          : '',
        possibleOptions: CiscoDialPlanManagementDnRangesFormComponent._isEANLocalRoutePartitionEnabledOrShowing(entity)
          ? entity.ent_alt_num_route_partition?.possibleOptions
          : [],
        required: CiscoDialPlanManagementDnRangesFormComponent._isEANLocalRoutePartitionEnabledOrShowing(entity)
          ? entity.ent_alt_num_route_partition?.required
          : false,
        show: CiscoDialPlanManagementDnRangesFormComponent._isEANLocalRoutePartitionEnabledOrShowing(entity)
          ? entity.ent_alt_num_route_partition?.show
          : false,
      },
      // E.164 Alternate Number
      e164_alt_num_number_mask: {
        name: entity.e164_alt_num_number_mask?.name,
        value: entity.e164_alt_num_number_mask?.value,
        required: entity.e164_alt_num_number_mask?.required,
        show: entity.e164_alt_num_number_mask?.show,
      },
      e164_alt_num_advertise_globally_via_ils: {
        enabled: entity.e164_alt_num_advertise_globally_via_ils?.value,
        show: entity.e164_alt_num_advertise_globally_via_ils?.show,
      },
      e164_alt_num_is_urgent: {
        enabled: entity.e164_alt_num_is_urgent?.value,
        show: entity.e164_alt_num_is_urgent?.show,
      },
      e164_alt_num_add_to_local_route_partition: {
        enabled: entity.e164_alt_num_add_to_local_route_partition?.value,
        show: entity.e164_alt_num_add_to_local_route_partition?.show,
      },
      e164_alt_num_route_partition: {
        name: entity.e164_alt_num_route_partition?.name,
        defaultOption: CiscoDialPlanManagementDnRangesFormComponent._isE164LocalRoutePartitionEnabledOrShowing(entity)
          ? entity.e164_alt_num_route_partition?.defaultOption
          : '',
        possibleOptions: CiscoDialPlanManagementDnRangesFormComponent._isE164LocalRoutePartitionEnabledOrShowing(entity)
          ? entity.e164_alt_num_route_partition?.possibleOptions
          : [],
        required: CiscoDialPlanManagementDnRangesFormComponent._isE164LocalRoutePartitionEnabledOrShowing(entity)
          ? entity.e164_alt_num_route_partition?.required
          : false,
        show: CiscoDialPlanManagementDnRangesFormComponent._isE164LocalRoutePartitionEnabledOrShowing(entity)
          ? entity.e164_alt_num_route_partition?.show
          : false,
      },
      // PSTN Failover
      pstn_failover: {
        name: entity.pstn_failover?.name,
        defaultOption:
          CiscoDialPlanManagementDnRangesFormComponent._getE164AltSectionPopulated(entity) ||
          CiscoDialPlanManagementDnRangesFormComponent._getEntAltSectionPopulated(entity)
            ? entity.pstn_failover?.defaultOption
            : '',
        possibleOptions:
          CiscoDialPlanManagementDnRangesFormComponent._getE164AltSectionPopulated(entity) ||
          CiscoDialPlanManagementDnRangesFormComponent._getEntAltSectionPopulated(entity)
            ? entity.pstn_failover?.possibleOptions
            : [],
        required:
          CiscoDialPlanManagementDnRangesFormComponent._getE164AltSectionPopulated(entity) ||
          CiscoDialPlanManagementDnRangesFormComponent._getEntAltSectionPopulated(entity)
            ? entity.pstn_failover?.required
            : false,
        show:
          CiscoDialPlanManagementDnRangesFormComponent._getE164AltSectionPopulated(entity) ||
          CiscoDialPlanManagementDnRangesFormComponent._getEntAltSectionPopulated(entity)
            ? entity.pstn_failover?.show
            : false,
      },
      enableAlternateExtension: entity.enableAlternateExtension,
      voicemailAlternateExtension: {
        name: CiscoFrontEndField.VOICEMAIL_ALTERNATE_EXTENSION,
        value: entity.voicemailAlternateExtension.value,
        required: true,
        show: false,
      },
    };
  };

  toEntity = (formData: CiscoDnRangesFormData): CiscoDnRangesFormEntity => {
    return {
      // DN Ranges
      ranges: formData.ranges,
      // Line Appearance Settings
      advertise_distinct_mask_for_public_lines: {
        value: formData.advertise_distinct_mask_for_public_lines,
        name: CiscoFrontEndField.ADVERTISE_DISTINCT_MASK_FOR_PUBLIC_LINES,
      },
      external_phone_number_mask: {
        name: formData.external_phone_number_mask.name,
        required: formData.external_phone_number_mask.required,
        show: formData.external_phone_number_mask.show,
        value: formData.external_phone_number_mask.value,
      },
      public_external_phone_number_mask: {
        name: formData.public_external_phone_number_mask.name,
        required: formData.public_external_phone_number_mask.required,
        show: formData.public_external_phone_number_mask.show,
        value: formData.public_external_phone_number_mask.value,
      },
      line_text_label: {
        name: formData.line_text_label.name,
        required: formData.line_text_label.required,
        show: formData.line_text_label.show,
        value: formData.line_text_label.value,
      },
      // Directory Number Settings
      route_partition: {
        name: formData.route_partition.name,
        defaultOption: formData.route_partition.defaultOption,
        possibleOptions: formData.route_partition.possibleOptions,
        required: formData.route_partition.required,
        show: formData.route_partition.show,
      },
      urgent_priority: {
        name: CiscoFrontEndField.URGENT_PRIORITY,
        value: formData.urgent_priority.enabled,
        show: formData.urgent_priority.show,
      },
      optional_line_group_membership: {
        name: formData.optional_line_group_membership.name,
        defaultOptions: formData.optional_line_group_membership.defaultOptions,
        possibleOptions: formData.optional_line_group_membership.possibleOptions,
        show: formData.optional_line_group_membership.show,
      },
      auto_answer: {
        name: formData.auto_answer.name,
        defaultOption: formData.auto_answer.defaultOption,
        possibleOptions: formData.auto_answer.possibleOptions,
        required: true,
        show: formData.auto_answer.show,
      },
      monitoring_calling_search_space: {
        name: formData.monitoring_calling_search_space.name,
        defaultOption: formData.monitoring_calling_search_space.defaultOption,
        possibleOptions: formData.monitoring_calling_search_space.possibleOptions,
        required: formData.monitoring_calling_search_space.required,
        show: formData.monitoring_calling_search_space.show,
      },
      // Enterprise Alternate Number
      ent_alt_num_number_mask: {
        name: formData.ent_alt_num_number_mask.name,
        required: formData.ent_alt_num_number_mask.required,
        show: formData.ent_alt_num_number_mask.show,
        value: formData.ent_alt_num_number_mask.value,
      },
      ent_alt_num_advertise_globally_via_ils: {
        name: CiscoFrontEndField.ADVERTISE_GLOBALLY,
        value: formData.ent_alt_num_advertise_globally_via_ils.enabled,
        show: formData.ent_alt_num_advertise_globally_via_ils.show,
      },
      ent_alt_num_is_urgent: {
        name: CiscoFrontEndField.IS_URGENT,
        value: formData.ent_alt_num_is_urgent.enabled,
        show: formData.ent_alt_num_is_urgent.show,
      },
      ent_alt_num_add_to_local_route_partition: {
        name: CiscoFrontEndField.ADD_TO_LOCAL_ROUTE_PARTITION,
        value: formData.ent_alt_num_add_to_local_route_partition.enabled,
        show: formData.ent_alt_num_add_to_local_route_partition.show,
      },
      ent_alt_num_route_partition: {
        name: formData.ent_alt_num_route_partition.name,
        defaultOption: formData.ent_alt_num_route_partition.defaultOption,
        possibleOptions: formData.ent_alt_num_route_partition.possibleOptions,
        required: formData.ent_alt_num_route_partition.required,
        show: formData.ent_alt_num_route_partition.show,
      },
      // E.164 Alternate Number
      e164_alt_num_number_mask: {
        name: formData.e164_alt_num_number_mask.name,
        required: formData.e164_alt_num_number_mask.required,
        show: formData.e164_alt_num_number_mask.show,
        value: formData.e164_alt_num_number_mask.value,
      },
      e164_alt_num_advertise_globally_via_ils: {
        name: CiscoFrontEndField.ADVERTISE_GLOBALLY,
        value: formData.e164_alt_num_advertise_globally_via_ils.enabled,
        show: formData.e164_alt_num_advertise_globally_via_ils.show,
      },
      e164_alt_num_is_urgent: {
        name: CiscoFrontEndField.IS_URGENT,
        value: formData.e164_alt_num_is_urgent.enabled,
        show: formData.e164_alt_num_is_urgent.show,
      },
      e164_alt_num_add_to_local_route_partition: {
        name: CiscoFrontEndField.ADD_TO_LOCAL_ROUTE_PARTITION,
        value: formData.e164_alt_num_add_to_local_route_partition.enabled,
        show: formData.e164_alt_num_add_to_local_route_partition.show,
      },
      e164_alt_num_route_partition: {
        name: formData.e164_alt_num_route_partition.name,
        defaultOption: formData.e164_alt_num_route_partition.defaultOption,
        possibleOptions: formData.e164_alt_num_route_partition.possibleOptions,
        required: formData.e164_alt_num_route_partition.required,
        show: formData.e164_alt_num_route_partition.show,
      },
      // PSTN Failover
      pstn_failover: {
        name: formData.pstn_failover.name,
        defaultOption: formData.pstn_failover.defaultOption,
        possibleOptions: formData.pstn_failover.possibleOptions,
        required: formData.pstn_failover.required,
        show: formData.pstn_failover.show,
      },
      // Voicemail Alternate Extension
      enableAlternateExtension: formData.enableAlternateExtension,
      voicemailAlternateExtension: {
        name: CiscoFrontEndField.VOICEMAIL_ALTERNATE_EXTENSION,
        value: formData.voicemailAlternateExtension.value,
        required: formData.enableAlternateExtension,
        show: formData.enableAlternateExtension,
      },
    };
  };

  setDisplayFormValidationState(): void {
    this.extensionRangesDisplayFormValidationState =
      this.isFormSubmitted || this.extensionRangesDisplayForm.extensionRanges.showValidation
        ? this.extensionRangesDisplayForm.validationState
        : this.validationStates.VALID;
  }

  onRangeFormUpdate(data: ZiroFormsUpdate<RangeDisplayFormModel>): void {
    // Always update validation state, don't always update entity. Async val will fire this multiple times so entity
    // won't necessarily change onFormUpdate
    if (isEqual(this.entity.ranges, data.new.ranges)) {
      this.setDisplayFormValidationState();
    } else {
      const newEntity = {
        ...cloneDeep(this.entity),
        ranges: data.new.ranges,
      };

      this.entitySource.next(newEntity);

      this.setDisplayFormValidationState();
    }
  }

  private _getSelectOptions(
    formValues: string[],
    validationTarget: ValidationTarget,
    availableOptions: string[],
  ): string[] {
    return formValues.filter((val: string) => {
      const validationState = this._invalidOptionValidator(val, availableOptions);

      return validationTarget === 'VALID'
        ? validationState === ZiroFormsValidationState.VALID
        : validationState === ZiroFormsValidationState.INVALID;
    });
  }

  private _invalidOptionValidator(val: string, availableOptions: string[]): ZiroFormsValidationState {
    return val && !availableOptions.includes(val) ? ZiroFormsValidationState.INVALID : ZiroFormsValidationState.VALID;
  }

  private _getCustomSelectAutogeneration(
    fieldId: keyof CiscoDnRangesFormData,
    availableSignalBasedOptions: string[] | null,
    isMultiSelect = false,
    optionsSignal: WritableSignal<any> = null,
  ): ZiroFormsAutogenerationConfig {
    return {
      linkLabel: 'tkey;shared.html.customizable_fields.text_input.fix_it.label',
      generateValue: (fieldValue) => {
        if (optionsSignal) {
          availableSignalBasedOptions = optionsSignal();
        }
        const validationStateSingleSelect =
          this._invalidOptionValidator(
            (this.formData[fieldId] as CustomSelect).defaultOption,
            availableSignalBasedOptions,
          ) === ZiroFormsValidationState.VALID;
        const defaultOptions = isMultiSelect
          ? this._getSelectOptions(
              (this.formData[fieldId] as CustomMultiSelect).defaultOptions,
              'VALID',
              availableSignalBasedOptions,
            )
          : [];

        const defaultOption = isMultiSelect
          ? defaultOptions[0]
          : validationStateSingleSelect
            ? (this.formData[fieldId] as CustomSelect).defaultOption
            : '';

        const select = this.formData[fieldId] as CustomSelect | CustomMultiSelect;

        const possibleOptions = this._getSelectOptions(
          (this.formData[fieldId] as CustomSelect | CustomMultiSelect).possibleOptions,
          'VALID',
          availableSignalBasedOptions,
        );

        if (isMultiSelect) {
          const updatedMultiSelect: CustomMultiSelect = {
            ...select,
            defaultOptions,
            possibleOptions: intersectionWith(possibleOptions, availableSignalBasedOptions),
          };
          this.entitySource.next({
            ...this.entity,
            [fieldId]: updatedMultiSelect,
          });
        } else {
          const updatedSelect: CustomSelect = {
            ...(select as CustomSelect),
            defaultOption,
            possibleOptions: intersectionWith(possibleOptions, availableSignalBasedOptions),
          };
          this.entitySource.next({
            ...this.entity,
            [fieldId]: updatedSelect,
          });
        }

        return fieldValue;
      },
      hidden: () => {
        if (optionsSignal) {
          availableSignalBasedOptions = optionsSignal();
        }
        const invalidPossibleOptions = this._getSelectOptions(
          (this.formData[fieldId] as CustomSelect | CustomMultiSelect).possibleOptions,
          'INVALID',
          availableSignalBasedOptions,
        );
        if (isMultiSelect) {
          const invalidDefaultValues = this._getSelectOptions(
            (this.formData[fieldId] as CustomMultiSelect).defaultOptions,
            'INVALID',
            availableSignalBasedOptions,
          );

          return invalidDefaultValues.length === 0 && invalidPossibleOptions.length === 0;
        } else {
          const validationState = this._invalidOptionValidator(
            (this.formData[fieldId] as CustomSelect).defaultOption,
            availableSignalBasedOptions,
          );

          return validationState !== ZiroFormsValidationState.INVALID && invalidPossibleOptions.length === 0;
        }
      },
      inline: true,
    };
  }

  private _getCustomSelectValidation(
    fieldId: keyof CiscoDnRangesFormData,
    availableSignalBasedOptions: string[] | null,
    isMultiSelect = false,
    optionsSignal: WritableSignal<any> = null,
  ): ZiroFormsValidationConfig {
    return [
      {
        validator: (val: CustomSelect) => {
          if (val && val.required && !val.defaultOption) {
            return ZiroFormsValidationState.INVALID;
          }
          return ZiroFormsValidationState.VALID;
        },
        message: 'tkey;custom_select.required.all_defaults_required.error',
      },
      {
        validator: (select: CustomSelect | CustomMultiSelect) => {
          if (optionsSignal) {
            availableSignalBasedOptions = optionsSignal();
          }

          let formValues;
          if ('defaultOptions' in select) {
            formValues = union([...select.defaultOptions, ...select.possibleOptions]);
          } else {
            formValues = union([...select.possibleOptions, select.defaultOption]);
          }

          return this._getSelectOptions(formValues, 'INVALID', availableSignalBasedOptions).length
            ? ZiroFormsValidationState.INVALID
            : ZiroFormsValidationState.VALID;
        },
        message: () => {
          let formValues;
          if (isMultiSelect) {
            formValues = union([
              ...(this.formData[fieldId] as CustomMultiSelect).defaultOptions,
              ...(this.formData[fieldId] as CustomMultiSelect).possibleOptions,
            ]);
          } else {
            formValues = union([
              ...(this.formData[fieldId] as CustomSelect).possibleOptions,
              (this.formData[fieldId] as CustomSelect).defaultOption,
            ]);
          }

          const invalidOptions = this._getSelectOptions(formValues, 'INVALID', availableSignalBasedOptions);

          const translation = this._translateService.instant('tkey;select.invalid_options.label');

          return `${translation} ${invalidOptions.join(', ')}`;
        },
      },
      {
        validator: (val: CustomSelect) => {
          if (!val.show && val.possibleOptions.length > 1) {
            return ZiroFormsValidationState.WARNING;
          }
          return ZiroFormsValidationState.VALID;
        },
        message: 'tkey;custom_select.show.multiple_values.warning',
      },
    ];
  }

  private _watchForChanges(): void {
    /**
     * When Enterprise or E164 alternate sections update the options available for PSTN Failover should refresh
     */
    const sub = this.ziroFormsUpdate$.subscribe((changes: ZiroFormsUpdate<CiscoDnRangesFormEntity>) => {
      const entNumberMaskChanged = !isEqual(changes.new.ent_alt_num_number_mask, changes.old.ent_alt_num_number_mask);
      const entAdvertiseGloballyChanged = !isEqual(
        changes.new.ent_alt_num_advertise_globally_via_ils,
        changes.old.ent_alt_num_advertise_globally_via_ils,
      );
      const entIsUrgentChanged = !isEqual(changes.new.ent_alt_num_is_urgent, changes.old.ent_alt_num_is_urgent);
      const entAddToLocalRoutePartitionChanged = !isEqual(
        changes.new.ent_alt_num_add_to_local_route_partition,
        changes.old.ent_alt_num_add_to_local_route_partition,
      );
      const entAltChanged =
        entNumberMaskChanged || entAdvertiseGloballyChanged || entIsUrgentChanged || entAddToLocalRoutePartitionChanged;

      const e164NumberMaskChanged = !isEqual(
        changes.new.e164_alt_num_number_mask,
        changes.old.e164_alt_num_number_mask,
      );
      const e164AdvertiseGloballyChanged = !isEqual(
        changes.new.e164_alt_num_advertise_globally_via_ils,
        changes.old.e164_alt_num_advertise_globally_via_ils,
      );
      const e164IsUrgentChanged = !isEqual(changes.new.e164_alt_num_is_urgent, changes.old.e164_alt_num_is_urgent);
      const e164AddToLocalRoutePartitionChanged = !isEqual(
        changes.new.e164_alt_num_add_to_local_route_partition,
        changes.old.e164_alt_num_add_to_local_route_partition,
      );
      const e164AltChanged =
        e164NumberMaskChanged ||
        e164AdvertiseGloballyChanged ||
        e164IsUrgentChanged ||
        e164AddToLocalRoutePartitionChanged;

      if (entAltChanged || e164AltChanged) {
        const hasEnterprise = CiscoDialPlanManagementDnRangesFormComponent._getEntAltSectionPopulated(changes.new);
        const hasE164 = CiscoDialPlanManagementDnRangesFormComponent._getE164AltSectionPopulated(changes.new);

        this._refreshPstnFailover(hasEnterprise, hasE164);
      }

      // Scroll the now-visible alternate extension input into view when the toggle is enabled
      if (changes.new.enableAlternateExtension && changes.old.enableAlternateExtension === false) {
        setTimeout(() => {
          document.querySelectorAll(`[data-automation="dn-ranges-voicemail-alternate-extension"]`)[0].scrollIntoView();
        }, 50);
      }
    });
    this._subscriptions.add(sub);
  }

  private _refreshPstnFailover(hasEnterprise: boolean, hasE164: boolean): void {
    this.fieldIdSignalOptions.pstn_failover.update(() =>
      CiscoDialPlanManagementDnRangesFormComponent._getInitialPstnOptions(hasEnterprise, hasE164),
    );
    const field = this.fieldComponents.find((f) => f.fieldId() === String(CiscoFrontEndField.PSTN_FAILOVER));
    field.applyComponentConfig(
      new ZiroCustomSelectConfig({
        modalHeader: 'tkey;dialplanmanagement.admin.group.dnranges.pstn_failover.advertised_number.label',
        displayValues: null,
        availableOptions: this.fieldIdSignalOptions.pstn_failover(),
        isAlwaysRequired: false,
      }),
    );
    setTimeout(() => {
      const pstnSelect = this.childSelects.find(
        (c: ZiroCustomSelectComponent) => c.fieldId() === String(CiscoFrontEndField.PSTN_FAILOVER),
      );
      if (pstnSelect) {
        this.validateAllFields();
      }
    }, 100);
  }

  protected readonly ButtonStyles = ButtonStyles;
}
